/*
 * AppReducer
 *
 * The reducer takes care of our data. Using actions, we can
 * update our application state. To add a new action,
 * add it to the switch statement in the reducer function
 *
 */

import produce from 'immer';
import {
  SET_PROJECT_CONFIG,
  SET_ARCHIVE_MODE,
  SET_ARCHIVE_GRAYSCALE,
  SET_ARCHIVE_MODE_DATE,
  SET_RUNNING_ARCHIVE,
  SET_DEBUG_MODE,
  SET_EDITOR_MODE,
  SET_STATISTICS_MODE,
  LOGOUT_REQUEST,
  CHANGE_FORM,
  REQUEST_ERROR,
  CLEAR_ERROR,
  SHOW_KEYBOARD,
  SENDING_REQUEST,
  SET_AUTH,
  SET_INPUT_KEYBOARD,
  SET_UPPERCASE_KEYBOARD,
  SET_SETTINGS,
  UPDATE_APP_HASH
} from './constants';

// The initial state of the App
export const initialState = {
  PROJECT_NAME: '',
  config: {},
  loading: false,
  error: false,
  currentUser: false,
  archiveMode: false,
  archiveGrayscale: true,
  runningArchive: false,
  debugMode: false,
  statisticsMode: false,
  editorMode: false,
  archiveModeDate: '',
  /* Login */
  loginState: {
    formState: {
      username: '',
      password: ''
    },
    error: '',
    user: { username: 'logout', privilege: -1 },
    currentlySending: false,
    loggedIn: false,
    isAdmin: false,
    isSuperMegaAdmin: false,
    privilege: -1,
    role: '',
    id: 0,
    vnc: []
  },
  /* User settings */
  settings: [],
  appHash: '',
  /* Keyboard */
  keyboardState: {
    showKeyboard: false,
    dataset: null,
    input: null,
    uppercase: true
  }
};

/* eslint-disable default-case, no-param-reassign */
const appReducer = (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case SET_PROJECT_CONFIG:
        draft.config = action.project_config;
        draft.PROJECT_NAME = action.project_config.name;
        break;
      case SET_ARCHIVE_MODE:
        draft.archiveMode = action.archiveMode;
        if (!action.archiveMode) draft.runningArchive = false;
        break;
      case SET_ARCHIVE_GRAYSCALE:
        draft.archiveGrayscale = action.archiveGrayscale;
        break;
      case SET_RUNNING_ARCHIVE:
        draft.runningArchive = action.runningArchive;
        break;
      case SET_DEBUG_MODE:
        draft.debugMode = action.debugMode;
        break;
      case SET_STATISTICS_MODE:
        draft.statisticsMode = action.statisticsMode;
        break;
      case SET_EDITOR_MODE:
        draft.editorMode = action.editorMode;
        break;

      case SET_ARCHIVE_MODE_DATE:
        draft.archiveModeDate = action.archiveModeDate;
        break;

      /* Login */
      case CHANGE_FORM:
        draft.loginState.formState = action.newFormState;
        break;
      case SET_AUTH:
        draft.loginState.loggedIn = action.newAuthState;
        draft.loginState.user = action.user;
        draft.loginState.isAdmin = action?.user?.privilege > 2;
        draft.loginState.isSuperMegaAdmin = action?.user?.privilege >= 4;
        draft.loginState.privilege = action?.user?.privilege;
        draft.loginState.vnc = action?.user?.vnc;
        break;

      case SENDING_REQUEST:
        draft.loginState.currentlySending = action.sending;
        break;
      case LOGOUT_REQUEST:
        draft.loginState.loggedIn = false;
        draft.archiveMode = false;
        draft.archiveGrayscale = true;
        draft.runningArchive = false;
        draft.debugMode = false;
        draft.statisticsMode = false;
        draft.editorMode = false;
        draft.archiveModeDate = '';
        break;
      case SET_SETTINGS:
        draft.settings = action.settings;
        break;
      case REQUEST_ERROR:
        draft.loginState.error = action.error;
        break;
      case CLEAR_ERROR:
        draft.loginState.error = '';
        break;
      case SHOW_KEYBOARD:
        draft.keyboardState.showKeyboard = action.showKeyboard;
        break;
      case SET_INPUT_KEYBOARD:
        draft.keyboardState.input = action.input;
        break;
      case SET_UPPERCASE_KEYBOARD:
        draft.keyboardState.uppercase = action.uppercase;
        break;
      case UPDATE_APP_HASH:
        draft.appHash = action.appHash;
        break;
    }
  });

export default appReducer;
